import { useEffect, useState } from 'react';
import { Outlet, NavLink, useNavigate, Link } from 'react-router-dom';
import {
  LayoutDashboard, BookOpen, Users, ShoppingCart, BarChart3, Tag, KeyRound, Gift, Quote,
  LogOut, Menu, X as CloseIcon, ExternalLink,
} from 'lucide-react';
import { api, getUser, setUser, logout } from '../../lib/api';

const NAV = [
  { to: '/admin',                label: 'Dashboard',      icon: LayoutDashboard, end: true },
  { to: '/admin/courses',        label: 'Courses',        icon: BookOpen },
  { to: '/admin/users',          label: 'Users',          icon: Users },
  { to: '/admin/purchases',      label: 'Purchases',      icon: ShoppingCart },
  { to: '/admin/discount-codes', label: 'Discount codes', icon: Tag },
  { to: '/admin/referrals',      label: 'Referrals',      icon: Gift },
  { to: '/admin/testimonials',   label: 'Testimonials',   icon: Quote },
  { to: '/admin/reports',        label: 'Reports',        icon: BarChart3 },
  { to: '/admin/publish-keys',   label: 'Studio publishing', icon: KeyRound },
];

export default function AdminLayout() {
  const navigate = useNavigate();
  const [user, setLocalUser] = useState(getUser());
  const [checking, setChecking] = useState(true);
  const [mobileOpen, setMobileOpen] = useState(false);

  // Verify admin status by re-fetching /api/me on mount.
  // localStorage may have a stale is_admin if the user was just promoted via
  // make_admin.js — refetching catches that without forcing a re-login.
  useEffect(() => {
    if (!getUser()) {
      navigate('/login?next=/admin');
      return;
    }
    api('/api/me')
      .then(({ user }) => {
        setUser(user);
        setLocalUser(user);
        if (!user.is_admin) navigate('/dashboard');
      })
      .catch(() => navigate('/login?next=/admin'))
      .finally(() => setChecking(false));
  }, [navigate]);

  const signOut = () => {
    logout().then(() => navigate('/login'));
  };

  if (checking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-slate-500 text-sm">Verifying admin access…</div>
      </div>
    );
  }

  if (!user?.is_admin) return null;

  return (
    <div className="flex h-screen bg-slate-100">
      {/* Mobile overlay */}
      {mobileOpen && (
        <div
          className="fixed inset-0 bg-slate-900/50 z-40 lg:hidden"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`
          fixed inset-y-0 left-0 w-64 bg-slate-900 text-white flex flex-col z-50
          transition-transform duration-200 lg:static lg:translate-x-0
          ${mobileOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        `}
      >
        <div className="px-6 py-5 border-b border-slate-800 flex items-center justify-between">
          <Link to="/admin" className="flex items-center gap-2 font-bold text-lg">
            <BookOpen className="text-brand" size={20} />
            Learnable Admin
          </Link>
          <button
            onClick={() => setMobileOpen(false)}
            className="lg:hidden text-slate-400 hover:text-white"
          >
            <CloseIcon size={20} />
          </button>
        </div>

        <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              onClick={() => setMobileOpen(false)}
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                  isActive
                    ? 'bg-brand text-white'
                    : 'text-slate-400 hover:bg-slate-800 hover:text-white'
                }`
              }
            >
              <item.icon size={18} />
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="p-3 border-t border-slate-800">
          <Link
            to="/dashboard"
            className="flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm text-slate-400 hover:bg-slate-800 hover:text-white"
          >
            <ExternalLink size={16} />
            Learner view
          </Link>
          <button
            onClick={signOut}
            className="w-full flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm text-slate-400 hover:bg-red-900/30 hover:text-red-300 mt-1"
          >
            <LogOut size={16} />
            Sign out
          </button>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col overflow-hidden min-h-0">
        <header className="h-14 bg-white border-b border-slate-200 flex items-center justify-between px-4 lg:px-8">
          <button
            onClick={() => setMobileOpen(true)}
            className="lg:hidden text-slate-500"
          >
            <Menu size={20} />
          </button>
          <div className="hidden lg:block text-xs font-semibold uppercase tracking-wider text-slate-400">
            Admin Console
          </div>
          <div className="flex items-center gap-3 text-sm">
            <div className="hidden sm:flex flex-col items-end leading-tight">
              <span className="font-semibold text-slate-900">{user.full_name}</span>
              <span className="text-xs text-slate-500">{user.email}</span>
            </div>
            <div className="w-9 h-9 rounded-full bg-brand text-white flex items-center justify-center font-bold">
              {user.full_name?.charAt(0).toUpperCase() || 'A'}
            </div>
          </div>
        </header>

        <main className="flex-1 overflow-auto min-h-0">
          <Outlet context={{ user }} />
        </main>
      </div>
    </div>
  );
}
